/**
 * Chapter 5 Cue Choice Component - Gratitude Leaf (Gratitude)
 * v2
 * Trauma-informed gratitude activity offering two approaches:
 * Option 1: "Leaf of Thanks" — hand on heart, name one thing you're grateful for (original)
 * Option 2: "Warm Memory" — somatic imagery approach where the child closes their eyes
 *            and pictures a moment that felt warm or safe, letting the feeling land in
 *            the body without needing to produce words or name anything aloud
 *
 * Based on the same dual-path architecture as BreathingCueChoice (Chapter 1).
 */

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { X, Heart, Sun, Info } from "lucide-react";

interface Chapter5CueChoiceProps {
  onComplete: () => void;
  isCompleted: boolean;
  trackButton?: (buttonId: string, metadata?: any) => void;
}

/** Shows one of the two activity modes step-by-step */
function ActivityMode({ mode, onBack, onComplete }: { mode: 'thanks' | 'memory'; onBack: () => void; onComplete: () => void }) {
  const [currentStep, setCurrentStep] = useState(0);

  const thanksSteps = [
    "Take a slow breath and settle in.",
    "Chant together: \"Leaf of Thanks.\"",
    "Place one hand gently on your heart.",
    "Take one slow, soft breath.",
    "Say: \"Thank you for [something] right now.\""
  ];

  const memorySteps = [
    "Close your eyes, or let your gaze go soft.",
    "Picture a moment that felt warm or safe — any moment, big or small.",
    "Let that picture get clearer in your mind.",
    "Notice where you feel that warmth in your body — chest? Belly? Shoulders?",
    "Just stay with that feeling for a moment. No words needed."
  ];

  const steps = mode === 'thanks' ? thanksSteps : memorySteps;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-lg font-semibold text-green-800">
          {mode === 'thanks' ? (
            <><Heart className="w-5 h-5 text-green-500" /> Leaf of Thanks</>
          ) : (
            <><Sun className="w-5 h-5 text-yellow-500" /> Warm Memory</>
          )}
        </div>
      </div>

      <div className="bg-green-50 rounded-lg p-6 min-h-[120px] flex items-center justify-center border-2 border-green-200">
        <AnimatePresence mode="wait">
          <motion.p
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-xl text-gray-800 text-center leading-relaxed"
          >
            {steps[currentStep]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Animated visual */}
      <div className="flex justify-center">
        <motion.div
          className={`w-20 h-20 rounded-full ${
            mode === 'thanks'
              ? 'bg-gradient-to-br from-green-400 to-emerald-500'
              : 'bg-gradient-to-br from-yellow-300 to-amber-400'
          }`}
          animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.08, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2">
        {steps.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentStep(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentStep ? 'bg-green-600 w-6' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      <div className="flex justify-between items-center pt-2">
        <Button variant="ghost" onClick={onBack} className="text-gray-600 hover:text-gray-800">
          ← Back to choices
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.max(0, p - 1))} disabled={currentStep === 0} className="text-sm">Previous</Button>
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.min(steps.length - 1, p + 1))} disabled={currentStep === steps.length - 1} className="text-sm">Next</Button>
        </div>
      </div>

      {currentStep === steps.length - 1 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-2">
          <Button onClick={onComplete} className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-base font-medium">
            {mode === 'thanks' ? 'I gave thanks!' : 'I felt the warmth!'}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}

/** Parent/Educator info panel */
function EducatorInfo({ onClose }: { onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 space-y-3"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">For Parents & Educators</h4>
        </div>
        <button onClick={onClose} className="text-blue-400 hover:text-blue-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="text-sm text-blue-800 space-y-2">
        <p><strong>Why two approaches to gratitude?</strong></p>
        <p>For some children, expressing gratitude verbally can feel forced or hollow—especially if they are in a difficult emotional season or come from backgrounds where expressing warmth felt unsafe.</p>
        <p><strong>"Warm Memory"</strong> uses somatic imagery, a body-centered technique where the felt sense of a positive memory is accessed without needing to produce language. Neuroscience shows that positive memory recall activates the same reward pathways as verbal gratitude, and can be more accessible for children with developmental or trauma-related differences.</p>
        <p>Both paths cultivate the same capacity: noticing and savoring goodness.</p>
      </div>
    </motion.div>
  );
}

/** Main Chapter 5 Cue Choice Component */
export function Chapter5CueChoice({ onComplete, isCompleted, trackButton }: Chapter5CueChoiceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'thanks' | 'memory' | null>(null);
  const [showEducatorInfo, setShowEducatorInfo] = useState(false);

  const handleClose = () => {
    setSelectedMode(null);
    setShowEducatorInfo(false);
    setIsOpen(false);
  };

  const handleComplete = () => {
    onComplete();
    handleClose();
  };

  return (
    <>
      {/* Glowing trigger button */}
      <motion.button
        className="relative bg-green-100 p-8 rounded-full border-4 border-white/70 shadow-2xl backdrop-blur-sm w-24 h-24 flex items-center justify-center"
        onClick={() => {
          trackButton?.('gratitude_leaf_cue_open', { cue_name: 'Gratitude Leaf' });
          setIsOpen(true);
        }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          scale: [1, 1.25, 1],
          boxShadow: [
            "0 0 0 0 rgba(34, 197, 94, 0.7)",
            "0 0 0 30px rgba(34, 197, 94, 0)",
            "0 0 0 0 rgba(34, 197, 94, 0.7)",
          ],
        }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <Heart className="w-12 h-12 text-green-600" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-200 relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <div className="w-10 h-10 rounded-full bg-green-50 text-green-600 border-2 border-current flex items-center justify-center">
                    <Heart className="w-5 h-5" />
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={handleClose} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
                <div className="text-center px-16">
                  <h3 className="text-xl font-bold">
                    <span className="font-serif font-bold bg-gradient-to-r from-green-600 via-emerald-500 to-green-600 bg-clip-text text-transparent">Leaf of Thanks</span>
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {showEducatorInfo && <EducatorInfo onClose={() => setShowEducatorInfo(false)} />}

                  {!selectedMode && !showEducatorInfo && (
                    <motion.div key="choice" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                      <p className="text-center text-gray-700 mb-2">Choose the path that feels right in this moment:</p>
                      <button onClick={() => setShowEducatorInfo(true)} className="flex items-center gap-1 mx-auto text-sm text-blue-600 hover:text-blue-800 mb-4">
                        <Info className="w-4 h-4" /> For Parents/Educators
                      </button>

                      {/* Option 1 */}
                      <motion.button
                        onClick={() => { trackButton?.('gratitude_path_thanks', { cue_name: 'Gratitude Leaf' }); setSelectedMode('thanks'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl text-left hover:border-green-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                            <Heart className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-green-900 mb-1">Leaf of Thanks</h4>
                            <p className="text-sm text-green-700">Hand on heart, chant, and say one thing you're grateful for out loud</p>
                          </div>
                        </div>
                      </motion.button>

                      {/* Option 2 */}
                      <motion.button
                        onClick={() => { trackButton?.('gratitude_path_memory', { cue_name: 'Gratitude Leaf' }); setSelectedMode('memory'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl text-left hover:border-yellow-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0">
                            <Sun className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-amber-900 mb-1">Warm Memory</h4>
                            <p className="text-sm text-amber-700">Picture a moment that felt warm or safe, and let that feeling settle in your body — no words needed</p>
                          </div>
                        </div>
                      </motion.button>

                      <p className="text-center text-sm text-gray-500 mt-4">Gratitude lives in the heart, however you feel it.</p>
                    </motion.div>
                  )}

                  {selectedMode && !showEducatorInfo && (
                    <ActivityMode key={selectedMode} mode={selectedMode} onBack={() => setSelectedMode(null)} onComplete={handleComplete} />
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
